<template>
  <nav
    aria-label="Sidebar"
    class="hidden md:block md:flex-shrink-0 md:bg-gray-800 md:overflow-y-auto"
  >
    <div class="relative w-20 flex flex-col p-3 space-y-3">
      <div v-if="!isTemporary">
        <template v-for="(item, key) in navigation" :key="key">
          <router-link
            :to="item.path || ''"
            class="tooltip flex-shrink-0 inline-flex items-center justify-center h-14 w-14 rounded-lg text-gray-400 hover:bg-gray-700"
            exact
            exact-active-class="bg-gray-900 text-slate-100"
            :class="{
              'bg-gray-900 text-slate-100': [item.path, path].every((p) =>
                (p ?? '').includes('application'),
              ),
            }"
          >
            <span class="hidden rounded-md">{{ item.name }}</span>
            <component :is="item.icon" class="h-6 w-6" aria-hidden="true" />
          </router-link>
        </template>
      </div>
    </div>
  </nav>
</template>

<script lang="ts">
import {
  ClipboardIcon,
  ClipboardListIcon,
  CogIcon,
  CubeIcon,
  ExclamationIcon,
  FolderIcon,
  HomeIcon,
  KeyIcon,
  OfficeBuildingIcon,
  UserGroupIcon,
  UserIcon,
  UsersIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  IdentificationIcon,
} from '@heroicons/vue/outline'
import { computed, defineComponent, inject } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import type { RouteRecordNormalized } from 'vue-router'
import { useStore } from 'vuex'
import { ABILITY_TOKEN } from '@casl/vue'
import { Ability } from '@casl/ability'
import { useI18n } from 'vue-i18n'

type NavigationItem = {
  name: string
  icon: string
  path?: string
}

const icons: Record<string, string> = {
  HomeIcon: 'HomeIcon',
  OfficeBuildingIcon: 'OfficeBuildingIcon',
  ClipboardIcon: 'ClipboardIcon',
  FolderIcon: 'FolderIcon',
  CubeIcon: 'CubeIcon',
  UserIcon: 'UserIcon',
  ExclamationIcon: 'ExclamationIcon',
  CogIcon: 'CogIcon',
  UsersIcon: 'UsersIcon',
  DocumentTextIcon: 'DocumentTextIcon',
  CheckCircleIcon: 'CheckCircleIcon',
  IdentificationIcon: 'IdentificationIcon',
}

export default defineComponent({
  name: 'Sidebar',
  components: {
    CogIcon,
    KeyIcon,
    HomeIcon,
    CubeIcon,
    UserIcon,
    FolderIcon,
    ClipboardIcon,
    UserGroupIcon,
    ClipboardListIcon,
    ExclamationIcon,
    OfficeBuildingIcon,
    UsersIcon,
    DocumentTextIcon,
    CheckCircleIcon,
    IdentificationIcon,
  },
  setup() {
    const { t } = useI18n()
    const store = useStore()
    const route = useRoute()
    const router = useRouter()
    const ability = inject<Ability>(ABILITY_TOKEN)
    const authRole = computed(() => store.getters['user/authRole'])
    const path = computed(() => route.path)
    const isTemporary = computed(() => store.getters['user/isTemporary'])

    const routes = router.getRoutes().reduce((carry: RouteRecordNormalized[], r) => {
      if (r.meta.requiresAuth) {
        carry.push(r)
      }
      return carry
    }, [])

    const navigation = computed(() =>
      routes
        .filter((r) => !r.meta.hideInMenu)
        .reduce((carry: NavigationItem[], r) => {
          const navItem: NavigationItem = {
            name: t(String(r.name)),
            path: r.path,
            icon: icons[r.meta.icon as string],
          }

          if (!r.meta.roles || (r.meta.roles as string[]).length === 0) {
            carry.push(navItem)
            return carry
          }

          if (
            Array.isArray(r.meta.roles) &&
            r.meta.roles.length &&
            r.meta.roles.includes(authRole.value)
          ) {
            if (Array.isArray(r.meta.abilities) && r.meta.abilities.length) {
              if (r.meta.abilities.some((p) => ability?.can(p.action, p.name))) {
                carry.push(navItem)
              }
              return carry
            }
            carry.push(navItem)
          }
          return carry
        }, []),
    )

    return {
      path,
      navigation,
      isTemporary,
    }
  },
})
</script>
<style lang="scss" scoped>
.tooltip {
  &:hover {
    position: relative;
    span {
      display: block !important;
      position: fixed;
      left: 70px;
      color: #fff;
      background-color: rgb(55 65 81);
      padding: 8px;
      z-index: 9999 !important;
      font-size: 13px;
      font-weight: 100;
    }
  }
}
</style>
